import React from 'react'
import LoadingPanel from './loadingPanel';
import ArrowButton from '../../../../../img/icon-btn-arrow-right.svg';
import { useSelector } from 'react-redux'
import { useGetOnlineCourseInstanceQuery, useGetCoursesSectionQuery, useLazyRegisterOnlineCourseQuery } from '../api/courses';
import { completedPastOnlineCourses, getOnlineCourseCompletedText } from '../helpers/coursesHelper';

/**
 * 
 * @param {string} [loadText] - Text to display during loading animation
 * @param {boolean} [small] -  Display a smaller version of the loader
 * @param {boolean} [paused] -  Stop the animation from playing - this is useful when using the loader as a "skeleton" while waiting for a component to load in
 */
function OnlineRegisterButton({courseRecordId, darkMode = false}) {
    // get the future courses with the new RTK query
    // Using a query hook automatically fetches data and returns query values
    // set up refetching so we can bust the cache when accordions are opened and new data is written to WP for open seats and wailists
    const { currentData: onlineCourseCurrentData} = useGetOnlineCourseInstanceQuery(courseRecordId);

    // get the current active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)

    // get the current courses with the new RTK query
    const { currentData: currentCoursesCurrentData } = useGetCoursesSectionQuery({templateId: courseRecordId, sections: 'current'});
    // get the prior courses with the new RTK query
    const { currentData: priorCoursesCurrentData } = useGetCoursesSectionQuery({templateId: courseRecordId, sections: 'past'});

    // register user for the online course
    const [registerUser, {isLoading: registrationIsLoading, currentData: registrationCurrentData}] = useLazyRegisterOnlineCourseQuery();

    // after the user clicks to register and we get a response, if it was successful
    // we want to redirect to the cart page
    if(registrationCurrentData?.success && registrationCurrentData?.redirect_url){
        window.location.href = registrationCurrentData?.redirect_url;
    }

    // local variables from online course api call
    const canTakeCourse = onlineCourseCurrentData?.canTakeCourse;
    const firstModuleWPPostID = onlineCourseCurrentData?.firstModuleWPPostID;
    const canTakeCourseMessage = onlineCourseCurrentData?.canTakeCourseMessage;
    const productPermalinkOverride = activeCourseName + "/";
    // local variable from current courses api call, flags if the user is currently registered for the online course
    const currentOnlineCourse = currentCoursesCurrentData?.current?.current_course_data?.[0]?.isOnline;
    const courseExpired = (currentCoursesCurrentData?.current?.current_course_data?.[0]?.CourseExpirationDate * 1000) < new Date();

    /**
     * Register the user for the online course
     * @param {number} moduleWPPostID - The first module WP Post ID
     * @returns void
    */
    const registerClickHandler = (onlineCourseWPPostId, productPermalinkOverride) => {
        registerUser({onlineCourseWPPostId: onlineCourseWPPostId, productPermalinkOverride: productPermalinkOverride});
    }

    /**
     * Get the register button
     * @returns JSX
     */
    const getRegisterButton = () => {
        
        // if the user clicked the register button
        if(registrationIsLoading || registrationCurrentData?.success && registrationCurrentData?.redirect_url){
            return (
                <LoadingPanel loadText={'Adding to cart'}/>
            )
        // if the user is already registered for the course
        } else if(courseExpired){
            return (
                <>Your course has expired, please contact the Kois Center to continue your journey.</>
            )
        // if there is a message to show the user, usually this is a reason they can't register for the course
        } else if(currentOnlineCourse){
            return (
                <>Currently Registered as Participant</>
            )
        // if there is a message to show the user, usually this is a reason they can't register for the course
        } else if(canTakeCourseMessage){
            return (
                <>{canTakeCourseMessage}</>
            )
        // if they can't take the course but we don't have a message to show them.
        }else if(!canTakeCourse){
            return (
                <>Course is unavailable at this time.</>
            )
        // finally, if the user can register, show them that button!
        } else {
            return (
                <a onClick={() => registerClickHandler(firstModuleWPPostID, productPermalinkOverride)} className={`btn c-btn--${darkMode ? 'purple' : 'light'}`}>Register{getArrowButton()}</a>
            )
        }
    }

    const getArrowButton = () => {
        if(darkMode){
            return (
                <span className="btn-arrow"><ArrowButton /></span>
            )
        }
    }
    
    // Component level JSX return statement ******************************************************
    return (
        // if we dont have data and its fetching show the loading panel
        <>
            {(!currentCoursesCurrentData || !onlineCourseCurrentData || !priorCoursesCurrentData) &&
                <LoadingPanel loadText={'Loading'} />
            }
            {/* if we have data, show the register button */}
            {currentCoursesCurrentData && onlineCourseCurrentData && priorCoursesCurrentData && !completedPastOnlineCourses(priorCoursesCurrentData) &&
                getRegisterButton()
            }
            {/* if the user has already completed the online course */}
            {completedPastOnlineCourses(priorCoursesCurrentData) &&
                <p><b>{getOnlineCourseCompletedText(priorCoursesCurrentData)}</b></p>
            }
        </>
    )
}
export default OnlineRegisterButton;